import { api } from './index'

/**
 * Available variables that can be interpolated into custom error messages.
 * Keys are variable names, values are descriptions of what the variable represents.
 */
export type AvailableVariables = Record<string, string>

/**
 * A single error category with its configuration and optional customization.
 */
export type OrderingErrorCategory = {
  /** Unique identifier for this category (e.g., "order_already_placed") */
  id: string
  /** Human-readable name for display (e.g., "Order Already Placed") */
  display_name: string
  /** Explanation of when this error occurs */
  description: string
  /** Real examples of error messages that match this category */
  example_messages: string[]
  /** Variables that can be used in custom messages (e.g., {minimum}, {merchant_name}) */
  available_variables: AvailableVariables
  /** The merchant's custom message, if configured */
  custom_message: string | null
  /** Whether the message was created by AI or manually */
  source: 'ai' | 'manual' | null
}

/**
 * Response from GET /ordering_error_config
 */
export type OrderingErrorConfigResponse = {
  brand_tone: string | null
  categories: OrderingErrorCategory[]
}

/**
 * A single category customization to be saved.
 */
export type CategoryCustomization = {
  custom_message: string | null
  source?: 'ai' | 'manual' | null
}

/**
 * Payload for PATCH /ordering_error_config
 * Maps category IDs to their customizations.
 */
export type OrderingErrorConfigUpdatePayload = {
  customizations: Record<string, CategoryCustomization>
  brand_tone?: string
}

/**
 * Response from PATCH /ordering_error_config
 */
export type OrderingErrorConfigUpdateResponse = {
  success: boolean
}

/**
 * Payload for POST /ordering_error_config/generate_suggestions
 */
export type GenerateSuggestionsPayload = {
  brand_tone: string
}

/**
 * A single AI-generated suggestion
 */
export type AiSuggestion = {
  category_id: string
  custom_message: string
}

/**
 * Response from POST /ordering_error_config/generate_suggestions
 */
export type GenerateSuggestionsResponse = {
  suggestions: AiSuggestion[]
}

export const orderingErrorConfigApi = api.injectEndpoints({
  overrideExisting: false,
  endpoints: build => ({
    /**
     * Fetches the ordering error configuration for the current merchant.
     * Returns all error categories with default messages merged with
     * any custom messages the merchant has configured.
     */
    getOrderingErrorConfig: build.query<OrderingErrorConfigResponse, void>({
      query: () => 'ordering_error_config',
      providesTags: ['OrderingErrorConfig'],
    }),

    /**
     * Updates the ordering error configuration for the current merchant.
     * Only categories with custom messages need to be included in the payload.
     * Set custom_message to null to remove a customization.
     */
    updateOrderingErrorConfig: build.mutation<
      OrderingErrorConfigUpdateResponse,
      OrderingErrorConfigUpdatePayload
    >({
      query: payload => ({
        url: 'ordering_error_config',
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['OrderingErrorConfig'],
    }),

    /**
     * Generates AI suggestions for all error message categories.
     * Takes a brand tone description and returns customized messages.
     */
    generateSuggestions: build.mutation<
      GenerateSuggestionsResponse,
      GenerateSuggestionsPayload
    >({
      query: payload => ({
        url: 'ordering_error_config/generate_suggestions',
        method: 'POST',
        body: payload,
      }),
    }),
  }),
})

export const {
  useGetOrderingErrorConfigQuery,
  useUpdateOrderingErrorConfigMutation,
  useGenerateSuggestionsMutation,
} = orderingErrorConfigApi
